import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { UserDashboardData, UserDashboardResponse } from './types';

interface UseUserDashboardResult {
  data: UserDashboardData | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useUserDashboard = (): UseUserDashboardResult => {
  const user = useCurrentUser();
  const partner = user?.primaryPartner;
  const [data, setData] = useState<UserDashboardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    if (!partner?.id) return;

    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get<UserDashboardResponse>(
        `/api/v4/partners/${partner.id}/user_dashboard`
      );

      if (requestId !== requestIdRef.current) {
        return;
      }

      setData(response.data.data.attributes);
    } catch (err: any) {
      if (requestId !== requestIdRef.current) {
        return;
      }

      setError(err?.response?.data?.error || err?.message || 'Failed to load dashboard data');
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [partner?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    return () => {
      requestIdRef.current += 1;
    };
  }, []);

  return { data, loading, error, refetch: fetchData };
};
